
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigRequest} from './ConfigRequest';
import {FieldModelConfigRequest} from './ConfigRequest';
import {SwapSettingsProfileGetResponse} from './SwapSettingsProfileGetResponse';

/**
 * SwapSettingsProfileGetRequest struct
 */
class SwapSettingsProfileGetRequest extends ConfigRequest {
  /**
   * Initialize struct
   * @param {!ConfigRequest=} parent
   * @param {UUID=} id
   * @param {string=} name
   * @constructor
   */
  constructor (parent = new ConfigRequest(), id = null, name = null) {
    super()
    super.copy(parent)
    this.Id = id
    this.Name = name
  }

  /**
   * Copy struct (shallow copy)
   * @this {!SwapSettingsProfileGetRequest}
   * @param {!SwapSettingsProfileGetRequest} other Other struct
   * @returns {!SwapSettingsProfileGetRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.Id != null) {
      this.Id = new UUID(other.Id)
    } else {
      this.Id = null
    }
    if (other.Name != null) {
      this.Name = other.Name
    } else {
      this.Name = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!SwapSettingsProfileGetRequest}
   * @returns {!SwapSettingsProfileGetRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new SwapSettingsProfileGetRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new SwapSettingsProfileGetRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!SwapSettingsProfileGetRequest}
   * @param {!SwapSettingsProfileGetRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof SwapSettingsProfileGetRequest)) {
      throw new TypeError('Instance of SwapSettingsProfileGetRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!SwapSettingsProfileGetRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      Id: ((this.Id != null) ? this.Id.toString() : null),
      Name: ((this.Name != null) ? this.Name : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return SwapSettingsProfileGetRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!SwapSettingsProfileGetRequest} other Object value
   * @returns {!SwapSettingsProfileGetRequest} Created struct
   */
  static fromObject (other) {
    return new SwapSettingsProfileGetRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!SwapSettingsProfileGetRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return SwapSettingsProfileGetRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!SwapSettingsProfileGetRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6628
  }
}

export { SwapSettingsProfileGetRequest };

/**
 * Fast Binary Encoding SwapSettingsProfileGetRequest field model
 */
class FieldModelSwapSettingsProfileGetRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigRequest(buffer, 4 + 4)
    this.Id = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.Name = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.Id.fbeOffset + this.Id.fbeSize), buffer, this.Id.fbeOffset + this.Id.fbeSize)
  }

  /**
   * Get the ConfigRequest field model
   * @this {!FieldModelSwapSettingsProfileGetRequest}
   * @returns {!FieldModelConfigRequest} ConfigRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelSwapSettingsProfileGetRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelSwapSettingsProfileGetRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.Id.fbeSize + this.Name.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelSwapSettingsProfileGetRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.Id.fbeExtra + this.Name.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelSwapSettingsProfileGetRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelSwapSettingsProfileGetRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelSwapSettingsProfileGetRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6628
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelSwapSettingsProfileGetRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelSwapSettingsProfileGetRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Id.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Id.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Id.fbeSize

    if ((fbeCurrentSize + this.Name.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Name.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Name.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelSwapSettingsProfileGetRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelSwapSettingsProfileGetRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelSwapSettingsProfileGetRequest}
   * @param {!SwapSettingsProfileGetRequest} fbeValue Default value, defaults is new SwapSettingsProfileGetRequest()
   * @returns {!SwapSettingsProfileGetRequest} SwapSettingsProfileGetRequest value
   */
  get (fbeValue = new SwapSettingsProfileGetRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelSwapSettingsProfileGetRequest}
   * @param {!SwapSettingsProfileGetRequest} fbeValue SwapSettingsProfileGetRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Id.fbeSize) <= fbeStructSize) {
      fbeValue.Id = this.Id.get()
    } else {
      fbeValue.Id = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Id.fbeSize

    if ((fbeCurrentSize + this.Name.fbeSize) <= fbeStructSize) {
      fbeValue.Name = this.Name.get()
    } else {
      fbeValue.Name = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Name.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelSwapSettingsProfileGetRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelSwapSettingsProfileGetRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelSwapSettingsProfileGetRequest}
   * @param {!SwapSettingsProfileGetRequest} fbeValue SwapSettingsProfileGetRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelSwapSettingsProfileGetRequest}
   * @param {SwapSettingsProfileGetRequest} fbeValue SwapSettingsProfileGetRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.Id.set(fbeValue.Id)
    this.Name.set(fbeValue.Name)
  }
}

export { FieldModelSwapSettingsProfileGetRequest };

/**
 * Fast Binary Encoding SwapSettingsProfileGetRequest model
 */
class SwapSettingsProfileGetRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelSwapSettingsProfileGetRequest(this.buffer, 4)
  }

  /**
   * Get the SwapSettingsProfileGetRequest model
   * @this {!SwapSettingsProfileGetRequestModel}
   * @returns {!FieldModelSwapSettingsProfileGetRequest} model SwapSettingsProfileGetRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!SwapSettingsProfileGetRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!SwapSettingsProfileGetRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return SwapSettingsProfileGetRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!SwapSettingsProfileGetRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelSwapSettingsProfileGetRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!SwapSettingsProfileGetRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!SwapSettingsProfileGetRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!SwapSettingsProfileGetRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!SwapSettingsProfileGetRequestModel}
   * @param {!SwapSettingsProfileGetRequest} value SwapSettingsProfileGetRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!SwapSettingsProfileGetRequestModel}
   * @param {!SwapSettingsProfileGetRequest} value SwapSettingsProfileGetRequest value, defaults is new SwapSettingsProfileGetRequest()
   * @return {!object} Deserialized SwapSettingsProfileGetRequest value and its size
   */
  deserialize (value = new SwapSettingsProfileGetRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new SwapSettingsProfileGetRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new SwapSettingsProfileGetRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!SwapSettingsProfileGetRequestModel}
   * @param {!number} prev Previous SwapSettingsProfileGetRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { SwapSettingsProfileGetRequestModel };
SwapSettingsProfileGetRequest.__has_response = true;
SwapSettingsProfileGetRequest.__response_class = SwapSettingsProfileGetResponse;
